import { forwardRef } from "react";
import { Loader2 } from "lucide-react";
import cn, { focusRing } from "./cn";

// DESIGN.md › Components › Buttons. One primary per view; secondary for the
// alternatives; quiet for low-emphasis actions; danger only for the
// destructive confirmation itself.
const VARIANTS = {
  primary: "border-transparent bg-accent text-on-accent hover:bg-accent-hover active:bg-accent-active",
  secondary: "border-default bg-surface text-foreground hover:border-strong",
  warning: "border-warning bg-warning-subtle text-warning hover:-translate-y-px hover:shadow-panel active:translate-y-0",
  quiet: "border-transparent bg-transparent text-secondary hover:bg-hover hover:text-foreground",
  danger: "border-transparent bg-danger text-on-accent hover:bg-danger/90",
};

// Compact on desktop; at least 44 px tall on touch-sized screens.
const SIZES = {
  sm: "h-control-sm gap-1.5 px-3 text-control-sm max-md:min-h-touch",
  md: "h-control gap-2 px-3.5 text-control max-md:min-h-touch",
};

const ICON_SIZES = {
  sm: "h-control-sm w-8 max-md:min-h-touch max-md:min-w-11",
  md: "h-control w-9 max-md:min-h-touch max-md:min-w-11",
};

const base = cn(
  "inline-flex shrink-0 select-none items-center justify-center whitespace-nowrap rounded-control border",
  "transition-[background-color,border-color,color,box-shadow,transform] duration-150 disabled:cursor-not-allowed disabled:opacity-50",
  "[&_svg]:h-4 [&_svg]:w-4 [&_svg]:shrink-0",
  focusRing
);

/**
 * Button — `as` renders another element (a router Link) with the same look.
 * `loading` keeps the label, adds a spinner and blocks repeat activation.
 */
const Button = forwardRef(function Button(
  { as: Component = "button", variant = "secondary", size = "md", loading = false, disabled, icon: Icon, className, children, type, ...props },
  ref
) {
  const isButton = Component === "button";
  const inactive = disabled || loading;
  return (
    <Component
      ref={ref}
      data-ui="button"
      data-variant={variant}
      type={isButton ? type || "button" : undefined}
      disabled={isButton ? inactive : undefined}
      aria-disabled={!isButton && inactive ? true : undefined}
      aria-busy={loading || undefined}
      className={cn(base, VARIANTS[variant], SIZES[size], !isButton && inactive && "pointer-events-none opacity-50", className)}
      {...props}
    >
      {loading ? <Loader2 className="animate-spin motion-reduce:animate-none" aria-hidden="true" /> : Icon ? <Icon aria-hidden="true" /> : null}
      {children}
    </Component>
  );
});

/**
 * IconButton — an icon-only action. `label` is required: it is the accessible
 * name and the hover title.
 */
export const IconButton = forwardRef(function IconButton(
  { as: Component = "button", icon: Icon, label, variant = "quiet", size = "md", className, type, ...props },
  ref
) {
  if (process.env.NODE_ENV !== "production" && !label) {
    // eslint-disable-next-line no-console
    console.error("IconButton requires a `label` for its accessible name.");
  }
  const isButton = Component === "button";
  return (
    <Component
      ref={ref}
      data-ui="icon-button"
      data-variant={variant}
      type={isButton ? type || "button" : undefined}
      aria-label={label}
      title={label}
      className={cn(base, VARIANTS[variant], ICON_SIZES[size], "px-0", className)}
      {...props}
    >
      <Icon aria-hidden="true" />
    </Component>
  );
});

export default Button;
